/**
 * DiffRouter Component
 * 
 * Wählt die richtige Diff-Komponente basierend auf block_type.
 */

import type { BlockType, ContentBlockContent, SeoMetaContent } from '@/types/content-blocks'
import { TextDiff } from './TextDiff'
import { BulletsDiff } from './BulletsDiff'
import { SeoMetaDiff } from './SeoMetaDiff'

interface DiffRouterProps {
  blockType: BlockType
  oldContent: ContentBlockContent
  newContent: ContentBlockContent
  oldLabel?: string
  newLabel?: string
  viewMode?: 'split' | 'unified'
}

export function DiffRouter({
  blockType,
  oldContent,
  newContent,
  oldLabel = 'Alt',
  newLabel = 'Neu',
  viewMode = 'split',
}: DiffRouterProps) {
  switch (blockType) {
    case 'title':
    case 'description':
    case 'short_description':
      return (
        <TextDiff
          oldValue={formatTextContent(oldContent)}
          newValue={formatTextContent(newContent)}
          oldLabel={oldLabel}
          newLabel={newLabel}
          viewMode={viewMode}
        />
      )

    case 'bullets':
      return (
        <BulletsDiff
          oldBullets={formatBulletsContent(oldContent)}
          newBullets={formatBulletsContent(newContent)}
          oldLabel={oldLabel}
          newLabel={newLabel}
        />
      )

    case 'seo_meta':
      return (
        <SeoMetaDiff
          oldMeta={formatSeoMetaContent(oldContent)}
          newMeta={formatSeoMetaContent(newContent)}
          oldLabel={oldLabel}
          newLabel={newLabel}
        />
      )

    default:
      // Fallback to text diff with JSON
      return (
        <TextDiff
          oldValue={formatJsonContent(oldContent)}
          newValue={formatJsonContent(newContent)}
          oldLabel={oldLabel}
          newLabel={newLabel}
          viewMode={viewMode}
        />
      )
  }
}

// Helper functions to format content

function formatTextContent(content: ContentBlockContent): string {
  if (typeof content === 'string') {
    return content
  }
  return JSON.stringify(content, null, 2)
}

function formatBulletsContent(content: ContentBlockContent): string[] {
  if (Array.isArray(content)) {
    return content.map(b => String(b))
  }
  if (typeof content === 'string') {
    // Try to parse as JSON array
    try {
      const parsed = JSON.parse(content)
      if (Array.isArray(parsed)) {
        return parsed.map(b => String(b))
      }
    } catch {
      // Split by newlines
      return content.split('\n').filter(Boolean)
    }
  }
  return []
}

function formatSeoMetaContent(content: ContentBlockContent): SeoMetaContent {
  if (typeof content === 'object' && content !== null && !Array.isArray(content)) {
    return content as SeoMetaContent
  }
  if (typeof content === 'string') {
    try {
      return JSON.parse(content) as SeoMetaContent
    } catch {
      return { title: '', description: '' }
    }
  }
  return { title: '', description: '' }
}

function formatJsonContent(content: ContentBlockContent): string {
  if (typeof content === 'string') {
    return content
  }
  return JSON.stringify(content, null, 2)
}


